export const monthList = [
  { label: 'January', value: '1' },
  { label: 'February', value: '2' },
  { label: 'March', value: '3' },
  { label: 'April', value: '4' },
  { label: 'May', value: '5' },
  { label: 'June', value: '6' },
  { label: 'July', value: '7' },
  { label: 'August', value: '8' },
  { label: 'September', value: '9' },
  { label: 'October', value: '10' },
  { label: 'November', value: '11' },
  { label: 'December', value: '12' },
];

/**
 * Month order within an academic year. The academic year runs June → May,
 * so months are listed starting from June (6) and wrapping to May (5).
 */
const ACADEMIC_MONTH_ORDER = [6, 7, 8, 9, 10, 11, 12, 1, 2, 3, 4, 5];

/** Month at which a new academic year begins (June). */
const ACADEMIC_START_MONTH = 6;

/**
 * Returns the start year of the academic year that `date` falls in.
 * e.g. any date from Jun 1 2026 to May 31 2027 → 2026 (label "2026-2027").
 */
const getAcademicStartYear = (date: Date): number => {
  const month = date.getMonth() + 1; // 1-based
  const year = date.getFullYear();
  return month >= ACADEMIC_START_MONTH ? year : year - 1;
};

/**
 * Returns filtered month options based on the selected academic year name.
 *
 * The academic year runs June → May and is labelled by its start year,
 * e.g. "2026-2027" covers Jun 2026 → May 2027.
 *
 * Logic:
 * - Past academic year: show all 12 months (in academic order).
 * - Current academic year: show months from June up to (but not including)
 *   the current month — i.e. only completed months.
 * - Future academic year: no months yet.
 *
 * @param academicYearName - e.g. "2026-2027"
 * @returns Filtered month options in academic (June → May) order
 */
export const getFilteredMonths = (
  academicYearName: string
): { label: string; value: string }[] => {
  const today = new Date();
  const currentMonth = today.getMonth() + 1; // 1-based
  const currentAcademicStartYear = getAcademicStartYear(today);

  // Parse the selected academic year's start year (e.g. "2026-2027" → 2026)
  const selectedStartYear = parseInt(academicYearName.split('-')[0], 10);

  // Months in academic order, mapped back to their monthList entries.
  const orderedMonths = ACADEMIC_MONTH_ORDER.map((monthNum) =>
    monthList.find((m) => parseInt(m.value, 10) === monthNum)
  ).filter((m): m is { label: string; value: string } => Boolean(m));

  // Future academic year — no months available yet.
  if (selectedStartYear > currentAcademicStartYear) {
    return [];
  }

  // Past academic year — show all months in academic order.
  if (selectedStartYear < currentAcademicStartYear) {
    return orderedMonths;
  }

  // Current academic year — show completed months only, i.e. up to (but not
  // including) the current month, following academic (June → May) order.
  const currentIndex = ACADEMIC_MONTH_ORDER.indexOf(currentMonth);
  return orderedMonths.slice(0, currentIndex);
};

/**
 * Returns the default month id to preselect: the last completed month in the
 * current academic year (i.e. the month before the current one in academic
 * June → May order). Falls back to the current month if nothing is completed
 * yet (e.g. in June, start of the academic year).
 */
export const getDefaultMonthId = (): string => {
  const currentMonth = new Date().getMonth() + 1; // 1-based
  const currentIndex = ACADEMIC_MONTH_ORDER.indexOf(currentMonth);
  const previousIndex = currentIndex - 1;
  const monthNum =
    previousIndex >= 0
      ? ACADEMIC_MONTH_ORDER[previousIndex]
      : ACADEMIC_MONTH_ORDER[0];
  return String(monthNum);
};

// ── API response dropdown mapping helpers ────────────────────

export interface CategoryItem {
  id: string;
  category_name: string;
}

export interface ManagementItem {
  id: string;
  management: string;
}

export interface DesignationItem {
  id: string;
  stackholders: string;
}

export interface AdminDrpDwnsResult {
  cateList: CategoryItem[];
  managementList: ManagementItem[];
  veracityUtypes: DesignationItem[];
}

/**
 * Maps cateList from API response to dropdown options
 */
export const mapCategoryOptions = (
  cateList: CategoryItem[]
): { label: string; value: string }[] => {
  return cateList.map((item) => ({
    label: item.category_name,
    value: item.id,
  }));
};

/**
 * Maps managementList from API response to dropdown options
 */
export const mapManagementOptions = (
  managementList: ManagementItem[]
): { label: string; value: string }[] => {
  return managementList.map((item) => ({
    label: item.management,
    value: item.id,
  }));
};

/**
 * Maps veracityUtypes from API response to dropdown options (Designation)
 */
export const mapDesignationOptions = (
  veracityUtypes: DesignationItem[]
): { label: string; value: string }[] => {
  return veracityUtypes.map((item) => ({
    label: item.stackholders,
    value: item.id,
  }));
};
